import { useCallback, useEffect, useState } from 'react';
import { Button, Dialog, InlineError, Input, Textarea } from '../../components/ui';
import {
  useCreateSupplierOrganization,
  useUpdateSupplierOrganization,
  useUpdateSupplierProfile,
} from './hooks';
import { organizationErrorMessage } from './queries';
import { isValidCode, normalizeCode } from './types';
import type { SupplierOrganization } from './types';

/**
 * A factory's record: who they are, and how TAP works with them.
 *
 * One dialog serves creation (organization = null) and editing. The identity
 * fields — name, code, contact email — go through an ordinary RLS-guarded
 * update. The operational ones go through update_supplier_profile, which is
 * admin-only at the database, because they decide what a supplier is committed
 * to and what a PO says.
 *
 * The operational fields only appear when EDITING. A factory being created has
 * no orders and no PO to address, and asking for its sample turnaround before
 * anyone has worked with it invites a number somebody invented.
 *
 * Activation lives on the list, not here.
 */
/**
 * A window, or blank, or unusable.
 *
 * Three outcomes rather than two, so a bad value cannot be mistaken for
 * "cleared" — which would silently wipe a factory's agreed window because
 * somebody typed a letter.
 */
function windowOf(raw: string): number | null | 'bad' {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 1 && value <= 365 ? value : 'bad';
}

export function OrganizationDialog({
  open,
  organization,
  onClose,
}: {
  open: boolean;
  organization: SupplierOrganization | null;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [address, setAddress] = useState('');
  const [sampleDays, setSampleDays] = useState('');
  const [productionDays, setProductionDays] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  // The dialog is reused across rows: sync the fields to the row being
  // edited whenever it opens.
  useEffect(() => {
    if (open) {
      setName(organization?.name ?? '');
      setCode(organization?.code ?? '');
      setContactEmail(organization?.contact_email ?? '');
      setContactName(organization?.contact_name ?? '');
      setAddress(organization?.address ?? '');
      // Blank, not '0'. An empty box says "no factory default" — which is a
      // real setting and the one every existing factory currently has.
      setSampleDays(
        organization?.default_sample_due_days === null ||
          organization?.default_sample_due_days === undefined
          ? ''
          : String(organization.default_sample_due_days),
      );
      setProductionDays(
        organization?.default_production_days === null ||
          organization?.default_production_days === undefined
          ? ''
          : String(organization.default_production_days),
      );
      setFieldError(undefined);
    }
  }, [open, organization]);

  const finish = useCallback(() => {
    setFieldError(undefined);
    onClose();
  }, [onClose]);

  const creation = useCreateSupplierOrganization(finish);
  const update = useUpdateSupplierOrganization(finish);
  const profile = useUpdateSupplierProfile(() => undefined);
  const mutation = organization ? update : creation;

  const isPending = mutation.isPending;
  const resetCreate = creation.reset;
  const resetUpdate = update.reset;

  // Stable close keeps the Dialog focus trap from rebuilding per keystroke.
  const handleClose = useCallback(() => {
    if (isPending) {
      return;
    }
    resetCreate();
    resetUpdate();
    setFieldError(undefined);
    onClose();
  }, [isPending, resetCreate, resetUpdate, onClose]);

  function handleSubmit() {
    if (mutation.isPending) {
      return;
    }
    const normalizedCode = normalizeCode(code);
    if (name.trim() === '') {
      setFieldError('Enter an organization name.');
      return;
    }
    if (!isValidCode(normalizedCode)) {
      setFieldError('Codes are 2-32 characters: lowercase letters, numbers, and hyphens.');
      return;
    }
    const days = windowOf(sampleDays);
    const production = windowOf(productionDays);
    if (days === 'bad' || production === 'bad') {
      // Mirrors the database check. Zero would start a clock that is already
      // expired, which reads to a factory as a fault rather than a deadline.
      setFieldError('Each window must be a whole number between 1 and 365, or blank.');
      return;
    }
    setFieldError(undefined);
    const input = { name, code: normalizedCode, contactEmail };
    if (organization) {
      // Two writes, because they carry two permissions. The profile one is
      // fired first and not awaited for navigation: the identity update owns
      // closing the dialog, and duplicating that would close it twice.
      profile.mutate({
        id: organization.id,
        input: {
          contactName,
          address,
          defaultSampleDueDays: days,
          defaultProductionDays: production,
        },
      });
      update.mutate({ id: organization.id, input });
    } else {
      creation.mutate(input);
    }
  }

  const title = organization ? 'Edit supplier organization' : 'New supplier organization';

  return (
    <Dialog
      open={open}
      title={title}
      onClose={handleClose}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? 'Saving…' : organization ? 'Save changes' : 'Create organization'}
          </Button>
        </>
      }
    >
      <Input
        label="Organization name"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Input
        label="Code"
        hint="Stable identifier: lowercase letters, numbers, and hyphens."
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
      <Input
        label="Contact email"
        hint="Optional."
        type="email"
        value={contactEmail}
        onChange={(event) => setContactEmail(event.target.value)}
      />
      {organization === null ? null : (
        <>
          <Input
            label="Attn (who the PO is addressed to)"
            hint="Printed on the PO. Blank prints “Production Lead”."
            value={contactName}
            onChange={(event) => setContactName(event.target.value)}
          />
          <Textarea
            label="Factory address"
            rows={3}
            hint="Printed in the MANUFACTURER block on the PO, line for line."
            value={address}
            onChange={(event) => setAddress(event.target.value)}
          />
          {/* Two windows, because a factory makes two promises and can be
              excellent at one and hopeless at the other. One number averaged
              them into a figure that described neither. */}
          <Input
            label="Sample SLA (days)"
            type="number"
            min={1}
            hint="Days to send samples after they accept the PO. Blank uses the workspace default."
            value={sampleDays}
            onChange={(event) => setSampleDays(event.target.value)}
          />
          <Input
            label="Production SLA (days)"
            type="number"
            min={1}
            hint="Days to have the order ready for collection once you approve their samples. Measured to ready — not to shipped, which is TAP arranging the courier."
            value={productionDays}
            onChange={(event) => setProductionDays(event.target.value)}
          />
        </>
      )}
      {fieldError ? <InlineError>{fieldError}</InlineError> : null}
      {profile.isError ? <InlineError>{profile.error.message}</InlineError> : null}
      {mutation.isError ? (
        <InlineError>{organizationErrorMessage(mutation.error.message)}</InlineError>
      ) : null}
    </Dialog>
  );
}
